<template>
  <div class="mt-4 px-5">
    <div class="d-flex align-center">
      <h2>Disziplinen</h2>
      <v-chip small color="primary" class="ml-2">Beta</v-chip>
    </div>

    <!-- Ohne Berechtigung (403): z. B. Menü noch aus dem Cache oder URL direkt aufgerufen -->
    <v-alert v-if="locked" type="info" text class="mt-3">
      Die Disziplinen sind für dich (noch) nicht freigeschaltet. Sie befinden sich in der Beta-Phase und
      werden schrittweise für Nutzer und Vereine freigegeben.
      <div class="mt-2">
        <v-btn small outlined color="primary" to="/user/home">Zur Startseite</v-btn>
      </div>
    </v-alert>

    <template v-else>
    <v-alert type="info" text dense class="mt-3 mb-0">
      Die Disziplinen sind noch in der Beta-Phase. Du kannst sie schon voll nutzen: suchen, filtern und
      dir zu jeder Disziplin Waffe, Ablauf, Scheiben und Regeln ansehen. Die Angaben stammen aus den
      Sportordnungen der Verbände und werden laufend ergänzt. Wenn dir etwas fehlt oder nicht stimmt,
      freue ich mich über eine kurze Rückmeldung.
      <div class="mt-2">
        <v-btn small outlined color="primary" :to="feedbackLink">
          <v-icon small left>mdi-message-text-outline</v-icon>Rückmeldung geben
        </v-btn>
      </div>
    </v-alert>

    <v-row class="mt-2">
      <!-- Suche + Trefferliste -->
      <v-col v-show="!isMobile || !selectedId" cols="12" md="5" lg="4">
        <v-card class="elevation-1">
          <v-progress-linear v-if="loadingList" indeterminate color="primary" absolute top></v-progress-linear>
          <v-card-text class="pb-2">
            <v-text-field
                v-model="query"
                prepend-inner-icon="mdi-magnify"
                label="Nummer, Name, Kaliber, Distanz …"
                outlined
                dense
                clearable
                hide-details
                :autofocus="!isMobile"
            ></v-text-field>

            <div v-for="facet in facetDefs" :key="facet.key" class="mt-2">
              <div class="text-caption text--secondary">{{ facet.label }}</div>
              <v-chip-group v-model="filters[facet.key]" multiple column>
                <v-chip
                    v-for="option in facets[facet.key] || []"
                    :key="option.value"
                    :value="option.value"
                    :disabled="!option.count && !option.selected"
                    filter
                    small
                    outlined
                    active-class="primary--text"
                >
                  {{ option.label }}
                  <span class="ml-1 text--secondary">{{ option.count }}</span>
                </v-chip>
              </v-chip-group>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <div class="d-flex align-center px-4 py-2 text-caption text--secondary">
            {{ result.count }} von {{ result.total }} Disziplinen
            <v-spacer></v-spacer>
            <v-btn v-if="hasFilter" x-small text color="primary" @click="resetFilters">Zurücksetzen</v-btn>
          </div>
          <v-divider></v-divider>

          <v-list dense class="py-0 result-list" :class="{ 'result-list--scroll': !isMobile }">
            <v-list-item
                v-for="item in result.items"
                :key="item.id"
                :input-value="item.id === selectedId"
                color="primary"
                @click="select(item.id)"
            >
              <v-chip label small class="mr-3 number-chip font-weight-bold flex-shrink-0">{{ item.nummer }}</v-chip>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ itemMeta(item) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="my-0 flex-row align-center">
                <v-icon v-if="item.auslauf_datum" small class="mr-1" :color="isExpired(item.auslauf_datum) ? 'error' : 'orange'"
                        :title="auslaufTitle(item.auslauf_datum)">mdi-calendar-remove</v-icon>
                <v-icon v-else-if="item.optional" small class="mr-1" color="orange" title="Optional">mdi-information-outline</v-icon>
                <v-icon small :color="levelColor(item.level)" :title="levelTitle(item.level)">{{ levelIcon(item.level) }}</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="!loadingList && !result.items.length">
              <v-list-item-content class="text-center text--secondary">
                Keine Disziplin gefunden. Weniger Wörter oder andere Filter probieren.
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Disziplinblatt -->
      <v-col v-show="!isMobile || selectedId" cols="12" md="7" lg="8">
        <DisciplineSheet
            :sheet="sheet"
            :loading="loadingSheet"
            :show-back="isMobile"
            @select="select"
            @back="closeSheet"
        />
      </v-col>
    </v-row>
    </template>
  </div>
</template>

<script>
import DisciplineSheet from "@/views/component/DisciplineSheet";

// Auslaufdatum (JJJJ-MM-TT) liegt vor heute
const isExpired = (date) => !!date && date < new Date().toLocaleDateString("sv-SE");
const LEVELS = { 3: "Regeldaten vollständig", 2: "Teilweise erfasst", 1: "Nur Grunddaten" };

export default {
  name: "disciplines",
  components: { DisciplineSheet },

  data: () => ({
    query: "",
    filters: { verband: [], group: [], dist: [] },
    facetDefs: [
      { key: "verband", label: "Verband" },
      { key: "group", label: "Gruppe" },
      { key: "dist", label: "Distanz" },
    ],
    result: { items: [], count: 0, total: 0 },
    facets: {},
    sheet: null,
    loadingList: false,
    loadingSheet: false,
    searchTimer: null,
    searchSeq: 0,
    locked: false,
  }),

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    selectedId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    // Bezug mit der gerade offenen Disziplin, damit klar ist, worum es geht
    feedbackLink() {
      const bezug = this.sheet
        ? `Disziplinen (Beta) – ${this.sheet.bund.kurz} ${this.sheet.nummer} ${this.sheet.name}`
        : "Disziplinen (Beta)";
      return { path: "/contactus", query: { bezug } };
    },
    hasFilter() {
      return !!this.query || Object.values(this.filters).some((v) => v.length);
    },
  },

  watch: {
    query() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.search, 250);
    },
    filters: {
      deep: true,
      handler() {
        this.search();
      },
    },
    selectedId: {
      immediate: true,
      handler(id) {
        if (id) this.loadSheet(id);
        else this.sheet = null;
      },
    },
  },

  created() {
    this.search();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    async search() {
      // Nur die Antwort der letzten Anfrage zählt
      const seq = ++this.searchSeq;
      this.loadingList = true;
      try {
        const data = await this.$store.dispatch("UserApp/searchDisciplines", {
          q: this.query || undefined,
          ...this.filters,
        });
        if (seq !== this.searchSeq) return;
        this.result = data;
        this.facets = data.facets;
        // Auf breiten Schirmen gleich das erste Blatt zeigen
        if (!this.selectedId && !this.isMobile && data.items.length) {
          this.select(data.items[0].id, true);
        }
      } catch (error) {
        this.handleLoadError(error);
      } finally {
        if (seq === this.searchSeq) this.loadingList = false;
      }
    },

    async loadSheet(id) {
      this.loadingSheet = true;
      try {
        this.sheet = await this.$store.dispatch("UserApp/getDisciplineSheet", id);
      } catch (error) {
        this.sheet = null;
        this.handleLoadError(error);
      } finally {
        this.loadingSheet = false;
      }
    },

    // 403 = nicht freigeschaltet (Berechtigung UseDisziplinblatt): Hinweis statt Fehler,
    // und das gecachte Menü neu laden, damit der Eintrag verschwindet
    handleLoadError(error) {
      if (error?.response?.status !== 403) {
        this.$store.dispatch("UserApp/handleError", error);
        return;
      }
      if (!this.locked) {
        this.locked = true;
        this.$store.dispatch("UserAuth/getUserProfilData");
      }
    },

    select(id, replace = false) {
      if (id === this.selectedId) return;
      const target = { name: "/user/disziplinen", params: { id: String(id) } };
      (replace ? this.$router.replace(target) : this.$router.push(target)).catch(() => {});
    },

    closeSheet() {
      this.$router.push({ name: "/user/disziplinen" }).catch(() => {});
    },

    resetFilters() {
      this.query = "";
      this.filters = { verband: [], group: [], dist: [] };
    },

    itemMeta(item) {
      const verband = item.liste === "B" ? `${item.verband} · Liste B` : item.bund;
      return [verband, item.group || item.kategorie, item.distanz, item.kaliber].filter(Boolean).join(" · ");
    },

    isExpired,

    auslaufTitle(date) {
      const text = new Date(date).toLocaleDateString("de-DE");
      return isExpired(date) ? `ausgelaufen: ${text}` : `läuft aus: ${text}`;
    },

    levelColor(level) {
      return { 3: "success", 2: "orange", 1: "grey" }[level];
    },

    levelTitle(level) {
      return LEVELS[level];
    },

    levelIcon(level) {
      return { 3: "mdi-circle-slice-8", 2: "mdi-circle-slice-4", 1: "mdi-circle-outline" }[level];
    },
  },
};
</script>

<style scoped>
.number-chip {
  min-width: 64px;
  justify-content: center;
}
.result-list--scroll {
  max-height: calc(100vh - 420px);
  min-height: 240px;
  overflow-y: auto;
}
</style>
